import React, { useEffect, useState } from 'react';
import { getAdmin } from '../../utils/authUtil';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FaUsers, FaBriefcase, FaCheckCircle, FaGraduationCap } from 'react-icons/fa';
import { getDashboardStats, getDashboardCharts, getRecentActivities } from '../../api/dashboardApi';

const COLORS = ['#2F6E4F', '#B84A3A', '#C9973F'];

const DashboardPage = () => {
  const user = getAdmin();
  const [stats, setStats] = useState({ activeDrives: 0, registered: 0, appeared: 0, qualified: 0 });
  const [charts, setCharts] = useState({ barData: [], pieData: [] });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const [statsRes, chartsRes, actRes] = await Promise.all([
        getDashboardStats(),
        getDashboardCharts(),
        getRecentActivities()
      ]);
      setStats(statsRes.data || { activeDrives: 0, registered: 0, appeared: 0, qualified: 0 });
      setCharts(chartsRes.data || { barData: [], pieData: [] });
      setActivities(actRes.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch dashboard data. Please check your backend connection.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(); // Initial fetch
    
    // Polling every 30 seconds for live updates
    const intervalId = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary-custom" role="status"></div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">Welcome back, {user?.name || 'Admin'}!</h3>
          <p className="text-muted mb-0">Here is what's happening with your recruitment drives today.</p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      
      {/* Metric Cards */}
      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <div className="card custom-card border-0 shadow-sm p-4 d-flex flex-row align-items-center gap-3 border-start border-4 border-primary">
            <div className="bg-primary-custom text-white p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
              <FaBriefcase size={24} />
            </div>
            <div>
              <p className="text-muted mb-0 fw-semibold">Active Drives</p>
              <h3 className="fw-bold mb-0">{stats.activeDrives || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card custom-card border-0 shadow-sm p-4 d-flex flex-row align-items-center gap-3 border-start border-4 border-secondary">
            <div className="bg-secondary-custom text-white p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
              <FaUsers size={24} />
            </div>
            <div>
              <p className="text-muted mb-0 fw-semibold">Registered</p>
              <h3 className="fw-bold mb-0">{stats.registered || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card custom-card border-0 shadow-sm p-4 d-flex flex-row align-items-center gap-3 border-start border-4 border-warning">
            <div className="bg-accent-custom text-white p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
              <FaGraduationCap size={24} />
            </div>
            <div>
              <p className="text-muted mb-0 fw-semibold">Appeared</p>
              <h3 className="fw-bold mb-0">{stats.appeared || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card custom-card border-0 shadow-sm p-4 d-flex flex-row align-items-center gap-3 border-start border-4 border-success">
            <div className="bg-success text-white p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
              <FaCheckCircle size={24} />
            </div>
            <div>
              <p className="text-muted mb-0 fw-semibold">Qualified</p>
              <h3 className="fw-bold mb-0">{stats.qualified || 0}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        {/* Bar Chart */}
        <div className="col-lg-8">
          <div className="card custom-card border-0 shadow-sm p-4 h-100">
            <h5 className="fw-bold mb-4">Students per Drive</h5>
            <div style={{ height: '300px' }}>
              {charts.barData?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.barData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{fill: '#6c757d'}} axisLine={false} tickLine={false} />
                    <YAxis tick={{fill: '#6c757d'}} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{fill: 'transparent'}} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Bar dataKey="students" fill="var(--primary-color)" radius={[4, 4, 0, 0]} barSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="d-flex h-100 justify-content-center align-items-center text-muted">No chart data available from backend.</div>}
            </div>
          </div>
        </div>
        
        {/* Pie Chart */}
        <div className="col-lg-4">
          <div className="card custom-card border-0 shadow-sm p-4 h-100">
            <h5 className="fw-bold mb-4">Selection Percentage</h5>
            <div style={{ height: '300px' }}>
              {charts.pieData?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={charts.pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} fill="#8884d8" paddingAngle={5} dataKey="value">
                      {charts.pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="d-flex h-100 justify-content-center align-items-center text-muted">No chart data available from backend.</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Recent Registrations Table */}
        <div className="col-12">
          <div className="card custom-card border-0 shadow-sm p-0 overflow-hidden">
            <div className="p-4 border-bottom d-flex justify-content-between align-items-center bg-white">
              <h5 className="fw-bold mb-0">Recent Activities</h5>
              <Link to="/admin/results" className="btn btn-sm btn-outline-secondary">View All Activities</Link>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="bg-light text-muted">
                  <tr>
                    <th className="py-3 px-4 border-0">Student Name</th>
                    <th className="py-3 px-4 border-0">Drive</th>
                    <th className="py-3 px-4 border-0">Date</th>
                    <th className="py-3 px-4 border-0">Status</th>
                    <th className="py-3 px-4 border-0 text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activities?.length > 0 ? activities.map((act) => (
                    <tr key={act.id}>
                      <td className="py-3 px-4 fw-semibold">{act.studentName}</td>
                      <td className="py-3 px-4">{act.driveName}</td>
                      <td className="py-3 px-4">{new Date(act.date).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <span className={`badge ${act.status === 'Completed' ? 'bg-success' : act.status === 'Violated' ? 'bg-danger' : 'bg-warning text-dark'} bg-opacity-10 border rounded-pill px-3 py-2`}>
                          {act.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-end">
                        <Link to={`/admin/drives/${act.driveId}/results/${act.studentId}`} className="btn btn-sm btn-light text-primary">Review</Link>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="py-4 text-center text-muted">No recent activities found in backend.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
